/**
 * Builds a {@link MutationPlan} from a {@link MutationInput}.
 */
import type { MutationInput } from '../input/input-types';
import type { FieldType, UpdateAction } from '../input/input-types';
import { buildFilterNode } from './input-to-plan';
import { AssignmentNode, MutationPlan } from './mutation-plan';
import { FieldRefNode, SourceNode } from './query-plan';

export function inputToMutationPlan(input: MutationInput): MutationPlan {
    const source = new SourceNode(
        '$.source.name',
        input.source.name,
        input.source.schema ?? null,
    );

    if (input.operation === 'insert') {
        // Column order is taken from the first row; every row is aligned to it.
        const columns = Object.keys(input.rows[0] ?? {});
        const rows = input.rows.map((row) => columns.map((col) => row[col]));
        const conflictKeys = input.onConflict?.length ? input.onConflict : null;
        return new MutationPlan(
            '$',
            'insert',
            source,
            [],
            null,
            columns,
            rows,
            conflictKeys,
        );
    }

    const filter = input.filter
        ? buildFilterNode(input.filter, '$.filter')
        : null;

    const assignments =
        input.operation === 'update' ? buildAssignments(input) : [];

    return new MutationPlan('$', input.operation, source, assignments, filter);
}

/** Fold `set` sugar and granular `actions` into a single assignment list. */
function buildAssignments(input: {
    set?: Array<{ field: string; type: FieldType; value: unknown }>;
    actions?: UpdateAction[];
}): AssignmentNode[] {
    const assignments: AssignmentNode[] = [];

    (input.set ?? []).forEach((s, i) => {
        assignments.push(
            new AssignmentNode(
                `$.set[${i}]`,
                new FieldRefNode(`$.set[${i}]`, s.field, s.type),
                'set',
                s.value,
            ),
        );
    });

    (input.actions ?? []).forEach((action, i) => {
        const path = `$.actions[${i}]`;
        if (action.op === 'increment') {
            assignments.push(
                new AssignmentNode(
                    path,
                    new FieldRefNode(path, action.field, 'number'),
                    'increment',
                    action.by,
                ),
            );
        } else if (action.op === 'removeField') {
            assignments.push(
                new AssignmentNode(
                    path,
                    new FieldRefNode(path, action.field, 'string'),
                    'removeField',
                    null,
                ),
            );
        } else {
            // 'set' | 'append'
            assignments.push(
                new AssignmentNode(
                    path,
                    new FieldRefNode(path, action.field, action.type),
                    action.op,
                    action.value,
                ),
            );
        }
    });

    return assignments;
}
